/**
 * The one place Leaflet lives.
 *
 * Every map in the app renders through here, so the tile source, the attribution,
 * the marker styling and the "fit the view to the points" behaviour are defined
 * once. Markers are CSS divIcons (a coloured dot, optionally numbered) rather than
 * Leaflet's default image pins — that sidesteps the well-known broken marker-image
 * path under a bundler and lets a stop carry its status colour and sequence number
 * for free.
 *
 * Tiles come from OpenStreetMap (no key, no bill). They need a network connection
 * to load; on a dead signal the map shows grey tiles but the markers, the route
 * line and the Navigate links still work, and Navigate hands off to Google Maps.
 */

import { useEffect, type ReactNode } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface MapMarker {
  id: string;
  latitude: number;
  longitude: number;
  /** Fill colour of the dot. */
  color: string;
  /** Optional number/letter shown inside the dot (e.g. a stop sequence). */
  label?: string;
  /** Larger dot for emphasis (the next stop, a selected shop). */
  big?: boolean;
  /** 'pin' draws a tall teardrop marker (the customer map); 'dot' a small disc
   *  (route stops, which carry a sequence number). Defaults to 'dot'. */
  shape?: 'dot' | 'pin';
  popup?: ReactNode;
}

/** A tall, clearly-visible teardrop marker — a map pin, like Google Maps. */
function pin(color: string): L.DivIcon {
  return L.divIcon({
    className: 'mapdot-wrap',
    html: `<svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">`
      + `<path d="M15 1 C7 1 1 7 1 15 C1 26 15 41 15 41 C15 41 29 26 29 15 C29 7 23 1 15 1 Z" `
      + `fill="${color}" stroke="#ffffff" stroke-width="2"/>`
      + `<circle cx="15" cy="15" r="5.5" fill="#ffffff"/></svg>`,
    iconSize: [30, 42],
    iconAnchor: [15, 41],
    popupAnchor: [0, -38],
  });
}

function dot(color: string, label?: string, big?: boolean): L.DivIcon {
  const size = big ? 30 : 22;
  return L.divIcon({
    className: 'mapdot-wrap',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};`
      + `border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.35);color:#fff;`
      + `font-size:${big ? 13 : 11}px;font-weight:700;display:flex;align-items:center;`
      + `justify-content:center;">${label ?? ''}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

function depotIcon(): L.DivIcon {
  return L.divIcon({
    className: 'mapdot-wrap',
    html: '<div style="padding:2px 7px;border-radius:6px;background:#6d28d9;color:#fff;'
      + 'font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.35);">Depot</div>',
    iconSize: [54, 22],
    iconAnchor: [27, 11],
  });
}

/** Pans/zooms so every point is visible whenever the set of points changes. */
function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) {
      map.setView(points[0]!, 15);
    } else if (points.length > 1) {
      map.fitBounds(points, { padding: [36, 36] });
    }
  }, [JSON.stringify(points), map]);
  return null;
}

export function MapCanvas({
  markers, depot, route, className, fallbackCenter = [26.9124, 75.7873], // Jaipur
}: {
  markers: MapMarker[];
  depot?: { latitude: number; longitude: number } | null;
  route?: [number, number][];
  className?: string;
  fallbackCenter?: [number, number];
}) {
  const allPoints: [number, number][] = [
    ...(depot ? [[depot.latitude, depot.longitude] as [number, number]] : []),
    ...markers.map((m) => [m.latitude, m.longitude] as [number, number]),
  ];
  const center = allPoints[0] ?? fallbackCenter;

  return (
    <div className={className ?? 'mapcanvas'}>
      <MapContainer center={center} zoom={13} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {depot && <Marker position={[depot.latitude, depot.longitude]} icon={depotIcon()} />}
        {route && route.length > 1 && (
          <Polyline positions={route} pathOptions={{ color: '#2563eb', weight: 3, dashArray: '2 6' }} />
        )}
        {markers.map((m) => (
          <Marker key={m.id} position={[m.latitude, m.longitude]} icon={m.shape === 'pin' ? pin(m.color) : dot(m.color, m.label, m.big)}>
            {m.popup && <Popup>{m.popup}</Popup>}
          </Marker>
        ))}
        <FitBounds points={allPoints} />
      </MapContainer>
    </div>
  );
}
